import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Loader2, Bell, Building2, KeyRound, Copy, RefreshCw, Save, Sparkles, Terminal,
} from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Agency() {
  const { user, refreshUser } = useAuth();
  const isAgency = user?.effective_plan === "agency";

  const [emailAlerts, setEmailAlerts] = useState(user?.email_alerts ?? true);
  const [savingPref, setSavingPref] = useState(false);

  const [branding, setBranding] = useState({
    company_name: "", logo_url: "", primary_color: "#00B8D9", contact_email: "",
  });
  const [apiKey, setApiKey] = useState(null);
  const [loading, setLoading] = useState(isAgency);
  const [savingBrand, setSavingBrand] = useState(false);
  const [rotating, setRotating] = useState(false);

  useEffect(() => { setEmailAlerts(user?.email_alerts ?? true); }, [user]);

  const loadAgency = useCallback(() => {
    if (!isAgency) { setLoading(false); return; }
    Promise.all([api.get("/agency/branding"), api.get("/agency/api-key")])
      .then(([b, k]) => {
        if (b.data) setBranding((prev) => ({ ...prev, ...b.data }));
        setApiKey(k.data?.api_key || null);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [isAgency]);
  useEffect(() => { loadAgency(); }, [loadAgency]);

  const toggleEmail = async (val) => {
    setEmailAlerts(val);
    setSavingPref(true);
    try {
      await api.put("/settings/email-alerts", { enabled: val });
      await refreshUser();
      toast.success(val ? "Email alerts turned on." : "Email alerts turned off.");
    } catch (err) {
      setEmailAlerts(!val);
      toast.error(formatApiError(err.response?.data?.detail) || "Could not update preference.");
    } finally {
      setSavingPref(false);
    }
  };

  const saveBranding = async () => {
    setSavingBrand(true);
    try {
      await api.put("/agency/branding", branding);
      toast.success("Branding saved. New PDF reports will use your brand.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not save branding.");
    } finally {
      setSavingBrand(false);
    }
  };

  const generateKey = async () => {
    setRotating(true);
    try {
      const { data } = await api.post("/agency/api-key");
      setApiKey(data.api_key);
      await refreshUser();
      toast.success("API key generated.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not generate key.");
    } finally {
      setRotating(false);
    }
  };

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard.");
  };

  const field = "w-full bg-[#060B16] border border-slate-800 rounded-md px-3 py-2 text-sm text-white focus:border-cyanbrand outline-none transition-colors";

  return (
    <div className="min-h-screen text-white">
      <Navbar />
      <main className="pt-24 pb-20 max-w-3xl mx-auto px-6">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-cyanbrand">Settings</p>
        <h1 className="font-heading text-3xl font-semibold mt-2">Alerts & Agency tools</h1>

        {/* Email alerts — all users */}
        <section className="mt-8 bg-[#0C1424] border border-slate-800 rounded-lg p-6" data-testid="email-alerts-card">
          <div className="flex items-start justify-between gap-6">
            <div className="flex items-start gap-3">
              <Bell className="text-cyanbrand mt-0.5" size={22} />
              <div>
                <h2 className="font-heading text-lg font-semibold">Email alerts</h2>
                <p className="text-sm text-slate-400 mt-1 max-w-md">
                  Get an email the moment a monitored site's Security Score drops or a new high-risk
                  issue is found. Sent to <span className="text-slate-200">{user?.email}</span>.
                </p>
              </div>
            </div>
            <Switch
              data-testid="email-alerts-switch"
              checked={emailAlerts}
              disabled={savingPref}
              onCheckedChange={toggleEmail}
            />
          </div>
        </section>

        {/* Agency tier */}
        {!isAgency ? (
          <section className="mt-6 bg-[#0C1424] border border-slate-800 rounded-lg p-8 text-center" data-testid="agency-upsell">
            <Building2 className="text-cyanbrand mx-auto" size={30} />
            <h2 className="font-heading text-xl font-semibold mt-3">White-label & API access</h2>
            <p className="text-sm text-slate-400 mt-2 max-w-lg mx-auto">
              Upgrade to the <span className="text-cyanbrand font-semibold">Agency</span> plan to put your
              own logo and brand on client PDF reports, manage up to 10 sites, and scan programmatically
              via the Warden API.
            </p>
            <Link
              to="/billing"
              data-testid="agency-upgrade-link"
              className="inline-flex items-center gap-2 mt-6 bg-cyanbrand hover:bg-[#00B8D9] text-slate-900 font-semibold px-5 py-2.5 rounded-md transition-all"
            >
              <Sparkles size={16} /> Upgrade to Agency
            </Link>
          </section>
        ) : loading ? (
          <div className="mt-8 text-slate-400 flex items-center gap-2"><Loader2 className="animate-spin" size={18} /> Loading…</div>
        ) : (
          <>
            {/* White-label branding */}
            <section className="mt-6 bg-[#0C1424] border border-slate-800 rounded-lg p-6" data-testid="branding-card">
              <div className="flex items-center gap-3">
                <Building2 className="text-cyanbrand" size={22} />
                <h2 className="font-heading text-lg font-semibold">White-label reports</h2>
              </div>
              <p className="text-sm text-slate-400 mt-1">Your brand appears on every PDF report you generate for clients.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-5">
                <div>
                  <label className="text-xs text-slate-400">Company name</label>
                  <input data-testid="branding-company-input" className={field} value={branding.company_name}
                    onChange={(e) => setBranding({ ...branding, company_name: e.target.value })} placeholder="Acme Web Studio" />
                </div>
                <div>
                  <label className="text-xs text-slate-400">Contact email</label>
                  <input data-testid="branding-contact-input" className={field} value={branding.contact_email}
                    onChange={(e) => setBranding({ ...branding, contact_email: e.target.value })} placeholder="hello@acme.com" />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-xs text-slate-400">Logo URL</label>
                  <input data-testid="branding-logo-input" className={field} value={branding.logo_url}
                    onChange={(e) => setBranding({ ...branding, logo_url: e.target.value })} placeholder="https://acme.com/logo.png" />
                </div>
                <div>
                  <label className="text-xs text-slate-400">Accent color</label>
                  <div className="flex items-center gap-3 mt-1">
                    <input type="color" data-testid="branding-color-input" value={branding.primary_color}
                      onChange={(e) => setBranding({ ...branding, primary_color: e.target.value })}
                      className="h-9 w-14 bg-transparent border border-slate-800 rounded cursor-pointer" />
                    <span className="text-sm text-slate-400">{branding.primary_color}</span>
                  </div>
                </div>
              </div>
              <button data-testid="save-branding-button" onClick={saveBranding} disabled={savingBrand}
                className="mt-6 inline-flex items-center gap-2 bg-cyanbrand hover:bg-[#00B8D9] text-slate-900 font-semibold px-5 py-2.5 rounded-md transition-all disabled:opacity-60">
                {savingBrand ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />} Save branding
              </button>
            </section>

            {/* API access */}
            <section className="mt-6 bg-[#0C1424] border border-slate-800 rounded-lg p-6" data-testid="api-key-card">
              <div className="flex items-center gap-3">
                <KeyRound className="text-cyanbrand" size={22} />
                <h2 className="font-heading text-lg font-semibold">API access</h2>
              </div>
              <p className="text-sm text-slate-400 mt-1">Scan sites programmatically. Keep this key secret.</p>

              {apiKey ? (
                <div className="mt-4 flex items-center gap-2 bg-[#060B16] border border-slate-800 rounded-md px-3 py-2">
                  <code data-testid="api-key-value" className="text-sm text-cyanbrand break-all flex-1">{apiKey}</code>
                  <button data-testid="copy-api-key-button" onClick={() => copy(apiKey)} className="text-slate-400 hover:text-white p-1"><Copy size={16} /></button>
                </div>
              ) : (
                <p className="mt-4 text-sm text-slate-500">No API key yet.</p>
              )}

              <button data-testid="generate-api-key-button" onClick={generateKey} disabled={rotating}
                className="mt-4 inline-flex items-center gap-2 border border-slate-700 hover:border-cyanbrand text-white px-4 py-2 rounded-md transition-colors text-sm disabled:opacity-60">
                {rotating ? <Loader2 className="animate-spin" size={15} /> : <RefreshCw size={15} />}
                {apiKey ? "Regenerate key" : "Generate key"}
              </button>

              <div className="mt-6">
                <p className="text-xs text-slate-400 flex items-center gap-2 mb-2"><Terminal size={14} /> Example request</p>
                <pre className="bg-[#060B16] border border-slate-800 rounded-md p-4 text-xs text-slate-300 overflow-x-auto">
{`curl -X POST ${API_BASE}/v1/scan \\
  -H "X-API-Key: ${apiKey || "YOUR_API_KEY"}" \\
  -H "Content-Type: application/json" \\
  -d '{"domain":"example.com"}'`}
                </pre>
              </div>
            </section>
          </>
        )}
      </main>
    </div>
  );
}
